/**
 * @author        yontaek@zumper.com (Yon Taek Chung)
 * @copyright     Copyright (c) 2021, Zumper
 * @description   FlagButton
 */

import { Button, type ButtonProps, Divider, Text, useTheme } from '@chakra-ui/react'
import { Flag } from '@zcode-ui/icons'
import type { AnalyticsC } from '@zcode/nr-utils'
import type { Building, Listing, NormalListingData } from '@zcode/state-type'
import React, { useCallback, useState } from 'react'

export interface FlagButtonProps extends ButtonProps {
  analyticChannel: typeof AnalyticsC.FULL_DETAIL | typeof AnalyticsC.PERMANENT_ADDRESS
  entity: Building | Listing<NormalListingData>
  hasDivider?: boolean
  iconSize?: number
  isFlagged?: boolean
  isMobile?: boolean
  label: string
  stroke?: string
  strokeHover?: string
  text?: string
}

export const FlagButton = ({
  analyticChannel,
  entity,
  hasDivider,
  height,
  iconSize,
  isFlagged,
  isMobile,
  label,
  margin,
  onClick,
  padding,
  stroke,
  strokeHover,
  text,
  width,
  ...rest
}: FlagButtonProps) => {
  const { colors } = useTheme()
  const [hovered, setHovered] = useState(false)

  const onMouseOver = useCallback(() => !isMobile && setHovered(true), [isMobile])
  const onMouseOut = useCallback(() => !isMobile && setHovered(false), [isMobile])

  const hoverStroke = strokeHover ?? colors.gray[950]

  return (
    <React.Fragment>
      {hasDivider && <Divider width="100%" />}
      <Button
        _hover={{ background: 'none' }}
        aria-label={label}
        height={height}
        margin={margin ?? 0}
        onBlur={onMouseOut}
        onClick={onClick}
        onFocus={onMouseOver}
        onMouseOut={onMouseOut}
        onMouseOver={onMouseOver}
        padding={padding ?? 0}
        variant="ghost"
        width={width}
        {...rest}
      >
        <Flag
          fill={hovered ? hoverStroke : isFlagged ? colors.pink[400] : colors.gray[950]}
          fillOpacity="1"
          size={iconSize ?? 18}
          stroke={hovered ? hoverStroke : isFlagged ? colors.pink[400] : colors.gray[950]}
        />
        {text && (
          <Text as="span" fontWeight="medium" ml={2} variant="body16Md" verticalAlign="text-bottom">
            {text}
          </Text>
        )}
      </Button>
    </React.Fragment>
  )
}
